/**
 * Storage — refresh expired Dropbox tokens modal — per docs/specs/storage.md §6.
 *
 * support_ok. Shows count of expired tokens (from useDropboxTokenHealth)
 * and a short progress preview with expected outcomes.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-21-v3
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../components/modal';
import { RoleButton } from '../components/role-gate';
import { useToast } from '../components/toast';
import type { StorageRefreshResult } from '../../lib/types';
import { refreshExpiredDropboxTokens, useDropboxTokenHealth } from '../../lib/api';
import { getHttpErrorMessage } from '../../lib/http';

export function StorageRefreshModal({ open, onClose }: { open: boolean; onClose: () => void }) {
    const health = useDropboxTokenHealth();
    const h = health.data;
    const qc = useQueryClient();
    const { toast } = useToast();
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState<StorageRefreshResult | null>(null);

    const expired = h?.expired ?? 0;
    const expiring = h?.expiring_24h ?? 0;
    const expectedSuccess = Math.round(((h?.auto_refresh_success_pct ?? 0) / 100) * expired);
    const expectedFail = Math.max(0, expired - expectedSuccess);

    useEffect(() => {
        if (!open) {
            setResult(null);
        }
    }, [open]);

    const failed = result?.results.filter((row) => !row.success) ?? [];
    const success = result?.results.filter((row) => row.success) ?? [];

    const handleRefresh = async () => {
        setRunning(true);
        setResult(null);
        try {
            const result = await refreshExpiredDropboxTokens();
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admin', 'storage', 'dropbox'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'storage', 'kpis'] })
            ]);
            setResult(result);
            toast('Dropbox refresh batch finished', {
                kind: result.failed > 0 ? 'info' : 'success',
                detail: `Checked ${result.checked}, refreshed ${result.refreshed}, failed ${result.failed}`
            });
        } catch (error) {
            toast('Refresh failed', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Expired tokens could not be refreshed')
            });
        } finally {
            setRunning(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Refresh expired Dropbox tokens"
            subtitle="Batch attempt on all currently expired tokens."
            width={500}
            footer={
                <>
                    <button className="btn btn-ghost" onClick={onClose} disabled={running}>
                        Cancel
                    </button>
                    <RoleButton
                        scope="support_ok"
                        className="btn btn-primary"
                        onClick={handleRefresh}
                        disabled={running || expired === 0}
                    >
                        {running ? 'Refreshing…' : 'Run refresh'}
                    </RoleButton>
                </>
            }
        >
            {!h ? (
                <div className="empty-state">Loading token health…</div>
            ) : (
                <>
                    <dl className="kv-list" style={{ marginBottom: 10 }}>
                        <dt>Expired tokens</dt>
                        <dd className="mono text-danger">{expired.toLocaleString()}</dd>
                        <dt>Expiring &lt; 24h</dt>
                        <dd className="mono text-warning">{expiring.toLocaleString()}</dd>
                        <dt>Recent auto-refresh success</dt>
                        <dd className="mono">{h.auto_refresh_success_pct.toFixed(1)}%</dd>
                    </dl>
                    <div className="form-field" style={{ marginBottom: 0 }}>
                        <span className="form-label">Progress preview</span>
                        <div
                            style={{
                                padding: 10,
                                background: 'var(--bg-input)',
                                borderRadius: 6,
                                fontSize: 12,
                                color: 'var(--text-primary)'
                            }}
                        >
                            Based on recent success rate, expect roughly{' '}
                            <strong>{expectedSuccess.toLocaleString()}</strong> recovered and{' '}
                            <strong>{expectedFail.toLocaleString()}</strong> still requiring re-auth
                            by the user.
                        </div>
                        <div className="form-hint">
                            Per-user results are returned by the worker and logged server-side.
                        </div>
                    </div>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '12px 0' }}>
                        Last run: if this batch failed for any users, use the refresh action again
                        once they have re-authenticated in-app.
                    </p>
                </>
            )}

            {result && (
                <div className="form-field" style={{ marginTop: 12, marginBottom: 0 }}>
                    <span className="form-label">Per-user results</span>
                    <dl className="kv-list" style={{ marginBottom: 8 }}>
                        <dt>Checked</dt>
                        <dd className="mono">{result.checked}</dd>
                        <dt>Refreshed</dt>
                        <dd className="mono text-success">{result.refreshed}</dd>
                        <dt>Failed</dt>
                        <dd className="mono text-danger">{result.failed}</dd>
                    </dl>
                    {success.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                            <div className="form-hint" style={{ marginBottom: 6, fontWeight: 500 }}>
                                Recent successful refreshes
                            </div>
                            {success.slice(0, 5).map((row, index) => (
                                <div
                                    key={`${row.user_id}-${index}`}
                                    className="mono"
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr auto',
                                        gap: 8,
                                        padding: '4px 0'
                                    }}
                                >
                                    <span>{row.user_email}</span>
                                    <span className="text-success">OK</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {failed.length > 0 && (
                        <div>
                            <div className="form-hint" style={{ marginBottom: 6, fontWeight: 500 }}>
                                Failed refreshes
                            </div>
                            {failed.slice(0, 5).map((row, index) => (
                                <div
                                    key={`${row.user_id}-${index}`}
                                    className="mono"
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr auto',
                                        gap: 8,
                                        padding: '4px 0'
                                    }}
                                >
                                    <span>{row.user_email}</span>
                                    <span className="text-danger">
                                        {row.error || 'token refresh required'}
                                    </span>
                                </div>
                            ))}
                            {failed.length > 5 && (
                                <div className="form-hint" style={{ marginTop: 6 }}>
                                    Showing 5 of {failed.length} failed rows.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
            {result && !result.results.length && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12 }}>
                    No per-user outcomes were returned by the worker for this run.
                </div>
            )}
        </Modal>
    );
}
